import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Logger } from '../../../logger/Logger';

import { fetchCasinoConfigAction } from '../../apiActions/fetchCasinoConfigAction';
import { fetchEnvAction } from '../../apiActions/fetchEnvAction';
import { fetchPlayerConfigAction } from '../../apiActions/fetchPlayerConfigAction';
import { fetchPlayerConfigSyncAction } from '../../apiActions/fetchPlayerConfigSyncAction';
import { fetchTableConfigAction } from '../../apiActions/fetchTableConfigAction';
import { fetchUserInfoAction } from '../../apiActions/fetchUserInfoAction';
import { fetchVersionsAction } from '../../apiActions/fetchVersionsAction';
import type { BetType } from '../bets/types';

import type { AutoplayLimits, AutoplayStartOptions, AutoplaySliceType } from './types';
import { AutoplayStatus } from './types';

const initialState: AutoplaySliceType = {
  isOpen: false,
  isInProgress: false,
  autoplayStatus: AutoplayStatus.Stopped,
  savedBet: null,
  roundsLeft: 0,
  balanceChange: 0,
  singleWinLimit: null,
  balanceIncreaseLimit: null,
  balanceDecreaseLimit: null,
  isLaunchedMidRound: false,
};

export const autoplaySlice = createSlice({
  name: 'autoplay',
  initialState,
  reducers: {
    saveBet: (state, action: PayloadAction<BetType>) => {
      state.savedBet = action.payload;
    },
    start: (state, action: PayloadAction<AutoplayStartOptions>) => {
      state.isInProgress = true;
      state.balanceChange = 0;
      state.isLaunchedMidRound = action?.payload?.midRound || false;
      state.autoplayStatus = AutoplayStatus.Running;
    },
    stop: (state) => {
      state.isInProgress = false;
      state.balanceChange = 0;
      state.balanceIncreaseLimit = null;
      state.balanceDecreaseLimit = null;
      state.singleWinLimit = null;
      state.savedBet = initialState.savedBet;
      state.autoplayStatus = AutoplayStatus.Stopped;
    },
    /**
     * Used to resume from Paused state. It's different from "start" because it does not clear AP state.
     * Should not be used to start from Stopped state.
     */
    resume: (state) => {
      if (state.autoplayStatus === AutoplayStatus.Paused) {
        state.isInProgress = true;
        state.autoplayStatus = AutoplayStatus.Running;
      }
    },
    /**
     * Used to pause autoplay. It's different from "stop" because it does not clear the AP state and bets
     */
    pause: (state) => {
      if (state.autoplayStatus === AutoplayStatus.Running) {
        state.isInProgress = false;
        state.autoplayStatus = AutoplayStatus.Paused;
      }
    },

    updateRoundCount: (state, action: PayloadAction<number>) => {
      state.roundsLeft = action.payload;
    },
    updateBalanceChange: (state, action: PayloadAction<number>) => {
      state.balanceChange += action.payload;
    },
    setLimits: (state, action: PayloadAction<AutoplayLimits>) => {
      state.balanceChange = 0;
      state.singleWinLimit = action.payload.singleWinLimit || null;
      state.balanceIncreaseLimit = action.payload.balanceIncreaseLimit || null;
      state.balanceDecreaseLimit = action.payload.balanceDecreaseLimit || null;
    },
    decRoundCount: (state) => {
      state.roundsLeft -= 1;
      state.isLaunchedMidRound = false;
      if (state.roundsLeft < 0) {
        Logger.logFailure(
          'errors',
          'autoplay tried to go negative',
          'autoplay',
          Date.now(),
          1,
          'AutoplayNegative',
          true
        );
        state.isInProgress = false;
        state.balanceChange = 0;
        state.balanceIncreaseLimit = null;
        state.balanceDecreaseLimit = null;
        state.singleWinLimit = null;
        state.savedBet = initialState.savedBet;
        state.autoplayStatus = AutoplayStatus.Stopped;
      }
    },
    toggleOpen: (state) => {
      state.isOpen = !state.isOpen;
    },
  },
  extraReducers: (builder) => {
    builder
      // Errors in which we should stop autoplay as the game flow is interrupted
      .addCase(fetchCasinoConfigAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchEnvAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchPlayerConfigAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchPlayerConfigSyncAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchTableConfigAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchUserInfoAction.rejected, (state) => {
        state.isInProgress = false;
      })
      .addCase(fetchVersionsAction.rejected, (state) => {
        state.isInProgress = false;
      });
  },
});

export const autoplayActions = autoplaySlice.actions;
